import { useTheme } from '@emotion/react';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { useContext, useEffect, useMemo } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent, TileControlGroup, tileContentEdgeOffsetClassNames } from '~/ui/core/tile/Tile';
import type { VersionSelect as VersionSelectType } from '~/ui/domain/version-select/VersionSelect';
import { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import { useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { ContentSections } from '../components/Common';
import { Title } from '../components/SidepanelTitle';
import { DescriptionSectionSkeleton, TitleSkeleton } from '../components/SkeletonLoaders';
import { useFormattedVersions } from '../hooks/use-formatted-versions';
import { useIsSkillProficiencyVisible } from '../job-profile-section/skills/skill-proficiency-visibility';
import { useDraftJobItemDetails } from '../job-profile-section/use-draft-job-item-details';
import { ActiveLanguageProvider } from '../providers/ActiveLanguageProvider';
import { JSA_COMPARE_PROPOSAL_VERSION } from '../url-tile-payloads';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { CompareVersionHeader } from './CompareVersionHeader';
import { DescriptionComparisonSection } from './DescriptionComparisonSection';
import { SkillsComparisonSection } from './SkillsComparisonSection';
import { TasksComparisonSection } from './TasksComparisonSection';
import { useProposalCompare } from './use-proposal-compare';

const useCompareVersionData = (
    architectureId: string,
    itemId: string,
    itemType: 'job-level' | 'job-role',
    versionA: string,
    versionB: string,
    proposalBlockId: string | undefined,
): {
    readonly formattedVersions: readonly VersionSelectType[];
    readonly hasProposalError: boolean;
    readonly leftProfile: JobProfileComponent | undefined;
    readonly rightProfile: JobProfileComponent | undefined;
    readonly tmpVersion: VersionSelectType | null;
} => {
    const showsProposal = is.nonEmptyString(proposalBlockId) && versionB === JSA_COMPARE_PROPOSAL_VERSION;

    const { draftData } = useDraftJobItemDetails({
        itemId,
    });

    const { data: left } = useJobItemDetails({
        architectureId,
        itemId,
        itemType,
        version: versionA,
    });

    const { data: right } = useJobItemDetails({
        architectureId,
        // The proposal sentinel is not a fetchable version.
        enabled: !showsProposal,
        itemId,
        itemType,
        version: versionB,
    });

    const { hasProposalError, proposalVersion, proposedProfile } = useProposalCompare(proposalBlockId);

    const { data: versions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });

    const { formattedVersions, newTmpVersion } = useFormattedVersions(versions ?? []);

    const isDraftVersion = versionB === draftData?.versionInfo?.version?.toString();
    const draftAwareRightProfile = isDraftVersion ? draftData?.jobProfile : right?.jobProfile;

    if (proposalVersion !== null) {
        return {
            formattedVersions,
            hasProposalError,
            leftProfile: left?.jobProfile,
            rightProfile: showsProposal ? proposedProfile : draftAwareRightProfile,
            tmpVersion: proposalVersion,
        };
    }

    if (draftData !== undefined) {
        return {
            formattedVersions,
            hasProposalError: false,
            leftProfile: left?.jobProfile,
            rightProfile: draftAwareRightProfile,
            tmpVersion: newTmpVersion,
        };
    }

    return {
        formattedVersions,
        hasProposalError: false,
        leftProfile: left?.jobProfile,
        rightProfile: right?.jobProfile,
        tmpVersion: null,
    };
};

const CompareVersionLoadingState: FunctionComponent = () => {
    const theme = useTheme();

    return (
        <Tile data-testid="loading" style={{ position: 'relative', zIndex: 1 }}>
            <ScrollArea>
                <TileContent>
                    <div style={{ marginTop: theme.spacing['4xl'] }}>
                        <TitleSkeleton />
                    </div>
                    <div
                        style={{
                            display: 'grid',
                            gap: theme.spacing.m,
                            gridTemplateColumns: '1fr 1fr',
                            width: '100%',
                        }}
                    >
                        <div
                            style={{
                                background: theme.colors.gray[200],
                                borderRadius: theme.borderRadius.m,
                                height: theme.spacing['6xl'],
                            }}
                        />
                        <div
                            style={{
                                background: theme.colors.gray[200],
                                borderRadius: theme.borderRadius.m,
                                height: theme.spacing['6xl'],
                            }}
                        />
                    </div>
                    <ContentSections columns="one">
                        <div
                            style={{
                                display: 'grid',
                                gap: theme.spacing.m,
                                gridTemplateColumns: '1fr 1fr',
                            }}
                        >
                            <div>
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                            </div>
                            <div>
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                            </div>
                        </div>
                    </ContentSections>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

const VersionSelectorsGrid: FunctionComponent<{
    readonly formattedVersion: readonly VersionSelectType[];
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly placeholder: string;
    readonly proposalBlockId: string | undefined;
    readonly tmpVersion: VersionSelectType | null;
    readonly versionA: string;
    readonly versionB: string;
}> = ({ formattedVersion, itemId, itemType, placeholder, proposalBlockId, tmpVersion, versionA, versionB }) => {
    const { openCompareTile } = useJobArchitectureUrlTileActions();
    const { translate } = useContext(ApplicationContext);
    const versionsWithTmp = useMemo(
        () => [tmpVersion, ...formattedVersion].filter((version): version is VersionSelectType => version !== null),
        [tmpVersion, formattedVersion],
    );

    useEffect(() => {
        if (versionsWithTmp.find((version) => version.id === versionB) === undefined) {
            openCompareTile({
                profileId: itemId,
                proposalBlockId,
                type: itemType,
                versionA,
                versionB: versionA,
            });
        }
    }, [versionsWithTmp, itemId, itemType, proposalBlockId, versionA, versionB, openCompareTile]);

    return (
        <CompareSelectorsGrid
            left={
                <VersionSelect
                    ariaLabel={translate('pages.job-architecture-v2.job-profile-version-compare-left')}
                    currentVersion={formattedVersion.find((version) => version.id === versionA)}
                    disabled={formattedVersion.length === 0}
                    placeholder={placeholder}
                    variant="secondary"
                    versions={formattedVersion}
                    onSwitchVersion={(id: string): void => {
                        openCompareTile({
                            profileId: itemId,
                            proposalBlockId,
                            type: itemType,
                            versionA: id ?? '',
                            versionB,
                        });
                    }}
                />
            }
            right={
                <VersionSelect
                    ariaLabel={translate('pages.job-architecture-v2.job-profile-version-compare-right')}
                    currentVersion={versionsWithTmp.find((version) => version.id === versionB)}
                    disabled={formattedVersion.length === 0}
                    placeholder={placeholder}
                    variant="secondary"
                    versions={versionsWithTmp}
                    onSwitchVersion={(id: string): void => {
                        openCompareTile({
                            profileId: itemId,
                            proposalBlockId,
                            type: itemType,
                            versionA,
                            versionB: id,
                        });
                    }}
                />
            }
        />
    );
};

/** Two profiles side by side in the compare tile: title, sections and skills, each marked with what changed. */
export const CompareProfiles: FunctionComponent<{
    readonly leftProfile: JobProfileComponent;
    readonly onClose: () => void;
    readonly onLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly rightProfile: JobProfileComponent;
    /** The pickers for both sides, above the comparison. */
    readonly selectors: ReactNode;
    readonly showProficiency: boolean;
    /** Task titles of both sides in their order; the task section shows only when given. */
    readonly tasks?: { readonly left: readonly string[]; readonly right: readonly string[] };
    readonly title: string;
}> = ({
    leftProfile,
    onClose,
    onLanguageChange,
    preferredLanguage,
    rightProfile,
    selectors,
    showProficiency,
    tasks,
    title,
}) => {
    const theme = useTheme();

    return (
        <ActiveLanguageProvider preferredLanguage={preferredLanguage} profiles={[leftProfile, rightProfile]}>
            <Tile data-testid="compare-version" style={{ position: 'relative', zIndex: 1 }}>
                <ScrollArea>
                    <TileContent>
                        <CompareVersionHeader onClose={onClose} onLanguageChange={onLanguageChange} />
                        <Title style={{ marginTop: theme.spacing['4xl'] }}>{title}</Title>
                        {selectors}
                        <ContentSections columns="one">
                            <DescriptionComparisonSection leftProfile={leftProfile} rightProfile={rightProfile} />
                            {tasks === undefined ? null : (
                                <TasksComparisonSection left={tasks.left} right={tasks.right} />
                            )}
                            <SkillsComparisonSection
                                leftProfile={leftProfile}
                                rightProfile={rightProfile}
                                showProficiency={showProficiency}
                            />
                        </ContentSections>
                    </TileContent>
                </ScrollArea>
            </Tile>
        </ActiveLanguageProvider>
    );
};

/** Two version pickers side by side, as above a profile comparison. */
export const CompareSelectorsGrid: FunctionComponent<{ readonly left: ReactNode; readonly right: ReactNode }> = ({
    left,
    right,
}) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%' }}>
        <div>{left}</div>
        <div>{right}</div>
    </div>
);

// CompareVersionHeader needs the ActiveLanguageProvider, which needs loaded
// profiles — the expired state has neither, so it carries its own close button.
const CompareVersionProposalExpired: FunctionComponent<{ readonly onClose: () => void }> = ({ onClose }) => {
    const { translate } = useContext(ApplicationContext);

    return (
        <Tile data-testid="compare-proposal-expired" style={{ position: 'relative', zIndex: 1 }}>
            <TileContent>
                <div className={clsx('flex justify-start pt-tile-edge', tileContentEdgeOffsetClassNames)}>
                    <TileControlGroup>
                        <Button
                            aria-label={translate('common.close')}
                            iconOnly={true}
                            size="sm"
                            variant="secondary"
                            onClick={onClose}
                        >
                            <IconCrossSmall />
                        </Button>
                    </TileControlGroup>
                </div>
                <p className="mt-3xl text-sm text-text-secondary">
                    {translate('pages.job-architecture-v2.job-profile-compare-proposal-expired')}
                </p>
            </TileContent>
        </Tile>
    );
};

export const CompareVersion: FunctionComponent<{
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly onClose: () => void;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly proposalBlockId?: string;
    readonly versionA: string;
    readonly versionB: string;
}> = ({ architectureId, itemId, itemType, onClose, preferredLanguage, proposalBlockId, versionA, versionB }) => {
    const { translate } = useContext(ApplicationContext);
    const data = useCompareVersionData(architectureId, itemId, itemType, versionA, versionB, proposalBlockId);
    const { updateCompareTileLanguage } = useJobArchitectureUrlTileActions();
    const showProficiency = useIsSkillProficiencyVisible(itemType);

    if (data.hasProposalError && versionB === JSA_COMPARE_PROPOSAL_VERSION) {
        return <CompareVersionProposalExpired onClose={onClose} />;
    }

    if (is.undefined(data.leftProfile) || is.undefined(data.rightProfile)) {
        return <CompareVersionLoadingState />;
    }

    return (
        <CompareProfiles
            leftProfile={data.leftProfile}
            preferredLanguage={preferredLanguage}
            rightProfile={data.rightProfile}
            selectors={
                <VersionSelectorsGrid
                    formattedVersion={data.formattedVersions}
                    itemId={itemId}
                    itemType={itemType}
                    placeholder={translate('pages.job-architecture-v2.job-profile-version-placeholder')}
                    proposalBlockId={proposalBlockId}
                    tmpVersion={data.tmpVersion}
                    versionA={versionA}
                    versionB={versionB}
                />
            }
            showProficiency={showProficiency}
            title={translate('pages.job-architecture-v2.job-profile-compare-versions')}
            onClose={onClose}
            onLanguageChange={updateCompareTileLanguage}
        />
    );
};
